/* Per-product reviews widget + shared fetch cache.
 *
 * Renders into `[data-reviews-widget]` containers. Supports either the
 * old (deprecated) `.reviews-widget__*` class names AND the new
 * `.rw-*` class names from the standalone section. Class names are
 * decoupled from JS — the JS targets data-attrs everywhere.
 *
 * Also exposes a fetch-dedupe layer at `window.YukiReviews` so the
 * star-rating product block can share the same `/list` response and
 * avoid a duplicate network call on the product page.
 *
 * JSON-LD enrichment: looks for the Product schema by `@id` (set in
 * main-product.liquid) and splices in `aggregateRating` + `review`.
 * Runs once on first widget initialization per page.
 */
(function () {
  if (window.__reviewsWidgetInit) return;
  window.__reviewsWidgetInit = true;

  const API_BASE = '/apps/api/reviews';
  const DEFAULT_PER_PAGE = 10;

  // ── Shared fetch cache (also consumed by product-star-rating.js) ───
  // Cache key uniquely identifies (mode, target/type, page, perPage).
  // Cached as the resolved JSON OR an in-flight promise — both work
  // with await.
  const fetchCache = {};

  /** Product-mode fetch — backward-compatible with existing callers. */
  function fetchReviewsList(target, type, page, perPage) {
    return fetchByMode({ mode: 'product', target: target, type: type }, page, perPage);
  }

  /** Generic fetcher — selects the right endpoint by mode. Shop mode
   *  now supports pagination + min_rating + sort options for the
   *  dedicated /pages/shop-reviews page. */
  function fetchByMode(spec, page, perPage) {
    let key, url;
    if (spec.mode === 'shop') {
      const minRating = spec.min_rating || 4;
      const sort = spec.sort || 'featured';
      key = 'shop:' + minRating + ':' + sort + ':' + page + ':' + perPage;
      url = API_BASE + '/shop-list'
        + '?per_page=' + perPage
        + '&page=' + page
        + '&min_rating=' + minRating
        + '&sort=' + sort;
    } else {
      key = spec.type + ':' + spec.target + ':' + page + ':' + perPage;
      url = API_BASE + '/list?target=' + encodeURIComponent(spec.target)
        + '&type=' + encodeURIComponent(spec.type)
        + '&page=' + page
        + '&per_page=' + perPage;
    }
    if (fetchCache[key]) return fetchCache[key];
    const p = fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('reviews_list_' + res.status);
        return res.json();
      });
    fetchCache[key] = p;
    return p;
  }

  window.YukiReviews = window.YukiReviews || { fetchList: fetchReviewsList, fetchByMode: fetchByMode };

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-reviews-widget]').forEach(initWidget);
  });

  function initWidget(root) {
    const mode = root.dataset.mode === 'shop' ? 'shop' : 'product';
    const target = root.dataset.target;
    const type = root.dataset.type;
    const productSchemaId = root.dataset.productSchemaId || null;
    const perPage = parseInt(root.dataset.perPage || '', 10) || DEFAULT_PER_PAGE;
    const layout = root.dataset.layout === 'cards' ? 'cards' : 'list';
    const carousel = root.dataset.carousel === 'true';
    // Shop-mode tuning (defaults match the original "curated 4-5 star, photos-first" behavior)
    const minRating = parseInt(root.dataset.minRating || '4', 10) || 4;
    const sortMode = root.dataset.sort === 'newest' ? 'newest' : 'featured';
    // Optional "See all" tile appended to the end of a carousel — link
    // out to the dedicated reviews page when set.
    const seeAllUrl = root.dataset.seeAllUrl || '';
    // Dedicated reviews page reads ?page=N from URL on load.
    const urlPagination = root.dataset.urlPagination === 'true';
    // Product mode requires target + type. Shop mode doesn't.
    if (mode === 'product' && (!target || !type)) {
      root.style.display = 'none';
      return;
    }
    const spec = mode === 'shop'
      ? { mode: 'shop', min_rating: minRating, sort: sortMode }
      : { mode: 'product', target: target, type: type };

    // Initial page: from ?page= URL param if enabled, else 1.
    let initialPage = 1;
    if (urlPagination) {
      const p = new URLSearchParams(window.location.search).get('page');
      const n = p ? parseInt(p, 10) : 1;
      initialPage = Number.isFinite(n) && n >= 1 ? n : 1;
    }

    // Pick the CSS namespace based on which class is present (back-compat).
    const isNewNs = root.classList.contains('rw-section');
    const klass = (n) => isNewNs ? 'rw-' + n : 'reviews-widget__' + n;

    const state = { page: initialPage, perPage, reviews: [], total: 0, avg: 0, totalPages: 1, loading: false };

    fetchByMode(spec, initialPage, perPage)
      .then(function (data) {
        state.total = data.aggregate.count;
        state.avg = data.aggregate.avg_rating;
        state.totalPages = data.total_pages || 1;
        state.reviews = data.reviews;
        renderAll();
        // JSON-LD enrichment only for product-mode widgets — shop mode
        // doesn't have a Product schema to splice into.
        if (mode === 'product' && productSchemaId) injectIntoProductSchema(productSchemaId, state, data.reviews);
        // Dispatch a load event so tab containers can react (e.g., auto-
        // switch to Shop tab when product reviews are empty).
        root.dispatchEvent(new CustomEvent('reviews:loaded', {
          bubbles: true,
          detail: { mode: mode, total: state.total, avg: state.avg, target: target, type: type },
        }));
      })
      .catch(function (err) {
        console.warn('[reviews-widget] fetch failed:', err);
        const loadingEl = root.querySelector('[data-reviews-loading]');
        if (loadingEl) loadingEl.textContent = 'Reviews unavailable.';
        root.dispatchEvent(new CustomEvent('reviews:loaded', {
          bubbles: true,
          detail: { mode: mode, total: 0, avg: 0, error: true },
        }));
      });

    root.addEventListener('click', function (e) {
      // "View product" link — let the anchor navigate normally. Without
      // this short-circuit, the card-click below would intercept and
      // open the modal instead.
      if (e.target.closest('.rw-product-link')) return;
      const loadMore = e.target.closest('[data-reviews-load-more]');
      if (loadMore) { e.preventDefault(); loadMoreClick(); return; }
      // Card click → open the full-review modal. Photo clicks WITHIN the
      // card are intercepted here too (they're <img>s without anchors)
      // so we don't need a separate photo handler in the cards layout.
      const card = e.target.closest('[data-review-card]');
      if (card) {
        e.preventDefault();
        const idx = parseInt(card.dataset.cardIndex, 10);
        if (Number.isFinite(idx)) openModal(idx);
        return;
      }
      // List-layout photo click → existing lightbox.
      const thumb = e.target.closest('[data-review-photo]');
      if (thumb) { e.preventDefault(); openLightbox(thumb.dataset.fullSrc, thumb.querySelector('img') ? thumb.querySelector('img').alt : ''); return; }
    });

    async function loadMoreClick() {
      if (state.loading || state.page >= state.totalPages) return;
      state.loading = true;
      try {
        const data = await fetchByMode(spec, state.page + 1, perPage);
        state.reviews = state.reviews.concat(data.reviews);
        state.page += 1;
        renderList();
        renderPagination();
      } catch (err) {
        console.warn('[reviews-widget] load more failed:', err);
      } finally {
        state.loading = false;
      }
    }

    function renderAll() {
      const loadingEl = root.querySelector('[data-reviews-loading]');
      if (loadingEl) loadingEl.style.display = 'none';
      if (state.total === 0) {
        const empty = root.querySelector('[data-reviews-empty]');
        if (empty) empty.style.display = '';
        return;
      }
      renderAggregate();
      renderList();
      renderPagination();
    }

    function renderAggregate() {
      const aggEl = root.querySelector('[data-reviews-aggregate]');
      if (!aggEl) return;
      const stars = renderStars(state.avg, 'aggregate');
      const plural = state.total === 1 ? 'review' : 'reviews';
      // Inline "See all" link — shown when the section configured a
      // see-all URL (shop carousels). Sits right after the count so it
      // reads as one sentence: "★★★★★ 5.0 · 134 reviews · See all →".
      const seeAllLink = seeAllUrl
        ? '<a class="rw-aggregate-see-all" href="' + escapeAttr(seeAllUrl) + '">See all →</a>'
        : '';
      aggEl.innerHTML = ''
        + '<span class="' + klass('stars') + ' ' + (isNewNs ? 'rw-stars--aggregate' : '') + '">' + stars + '</span>'
        + '<span class="' + klass('avg') + '">' + (Math.round(state.avg * 10) / 10).toFixed(1) + '</span>'
        + '<span class="' + klass('count') + '">' + state.total + ' ' + plural + '</span>'
        + seeAllLink;
    }

    function renderList() {
      const listEl = root.querySelector('[data-reviews-list]');
      if (!listEl) return;
      if (layout === 'cards') {
        // Compose className: base + optional carousel modifier.
        let cls = isNewNs ? 'rw-cards' : 'reviews-widget__cards';
        if (carousel && isNewNs) cls += ' rw-cards--carousel';
        listEl.className = cls;
        let html = state.reviews.map(reviewCardHtml).join('');
        // In carousel mode, append a "See all reviews" tile as the LAST
        // item when a see-all URL is configured. It's a card with the
        // same dimensions as a review card so the row stays orderly.
        if (carousel && seeAllUrl) html += seeAllCardHtml(state.total);
        listEl.innerHTML = html;
        if (carousel) wireCarousel(listEl);
      } else {
        listEl.className = isNewNs ? 'rw-list' : 'reviews-widget__list';
        listEl.innerHTML = state.reviews.map(reviewItemHtml).join('');
      }
    }

    function seeAllCardHtml(total) {
      const count = total > 0 ? total.toLocaleString() : '';
      return ''
        + '<a class="rw-card rw-card--see-all" href="' + escapeAttr(seeAllUrl) + '" aria-label="See all shop reviews">'
        + '  <div class="rw-card--see-all__inner">'
        + '    <div class="rw-card--see-all__icon" aria-hidden="true">→</div>'
        + '    <div class="rw-card--see-all__label">See all shop reviews</div>'
        + (count ? '<div class="rw-card--see-all__count">' + count + ' reviews</div>' : '')
        + '  </div>'
        + '</a>';
    }

    /** Wire up the prev/next arrow buttons (rendered in the section's
     *  Liquid markup, NOT injected here) to scroll the list horizontally
     *  by roughly one viewport. Also toggles the disabled state on each
     *  arrow based on scroll position. Re-callable on every render. */
    function wireCarousel(listEl) {
      const prevBtn = root.querySelector('[data-rw-carousel-prev]');
      const nextBtn = root.querySelector('[data-rw-carousel-next]');
      if (!prevBtn || !nextBtn) return;

      function syncDisabled() {
        const maxScroll = listEl.scrollWidth - listEl.clientWidth;
        prevBtn.disabled = listEl.scrollLeft <= 2;
        nextBtn.disabled = listEl.scrollLeft >= maxScroll - 2;
      }

      function scrollBy(direction) {
        // Scroll by ~85% of the visible width so a sliver of the last
        // card stays visible as an "anchor" for the user's eye.
        const delta = Math.max(220, Math.floor(listEl.clientWidth * 0.85)) * direction;
        listEl.scrollBy({ left: delta, behavior: 'smooth' });
      }

      // Idempotent wire-up: avoid stacking listeners across re-renders.
      if (!prevBtn.dataset.rwWired) {
        prevBtn.addEventListener('click', () => scrollBy(-1));
        nextBtn.addEventListener('click', () => scrollBy(1));
        listEl.addEventListener('scroll', syncDisabled, { passive: true });
        // Recompute on viewport resize so disabled flips when wider screens
        // show the whole row at once.
        window.addEventListener('resize', syncDisabled);
        // Re-measure when the parent tab panel becomes visible. While
        // the panel is display:none (initial state for the Shop tab in
        // the product-page widget), listEl.clientWidth/scrollWidth are
        // both 0 → maxScroll = 0 → BOTH arrows render as disabled even
        // though there's plenty to scroll. The tabs JS dispatches
        // `reviews:visible` after flipping the is-hidden class; on
        // receipt we re-run syncDisabled with the now-correct widths.
        root.addEventListener('reviews:visible', function () {
          requestAnimationFrame(syncDisabled);
        });
        prevBtn.dataset.rwWired = '1';
      }
      // Always re-sync now in case new cards were appended.
      requestAnimationFrame(syncDisabled);
    }

    function reviewCardHtml(r, idx) {
      const stars = renderStars(r.rating, 'card');
      const date = formatDate(r.created_at);
      // Photo thumbnails (small, non-interactive — whole card is clickable).
      const photos = (r.images || []).slice(0, 5).map(function (img) {
        const onerr = "this.remove();";
        return '<img class="rw-card__photo" src="' + escapeAttr(img.cdn_url) + '" alt="" loading="lazy" decoding="async" onerror="' + onerr + '">';
      }).join('');
      const title = r.title ? '<h4 class="rw-card__title">' + escapeHtml(r.title) + '</h4>' : '';
      const sourceTag = r.source === 'etsy_import'
        ? '<span class="rw-source-tag" title="Imported from Etsy">via Etsy</span>' : '';
      const verifiedTag = r.verified_purchase
        ? '<span class="rw-verified-tag" title="Verified purchase">✓</span>' : '';
      // "View product" link — only when the review is attached to a Yuki
      // design/set. The card-click handler intercepts most clicks to open
      // the modal; the global handler explicitly lets clicks INSIDE
      // .rw-product-link pass through so the anchor navigates.
      const productLink = r.product_url
        ? '<a class="rw-product-link" href="' + escapeAttr(r.product_url) + '">View product →</a>' : '';
      // Product hero thumbnail — rendered INSIDE the card head as a
      // flex sibling of stars + date (same pattern as the list item)
      // so the head expands vertically to fit the thumb and the body /
      // title / photos below get full-width back. The previous
      // absolute-positioned version overlapped multi-line body text in
      // the upper-right region. Wrapped in the product anchor so
      // clicking the thumb navigates to the product page.
      const productThumb = r.product_image_url && r.product_url
        ? '<a class="rw-card__product-thumb rw-product-link" href="' + escapeAttr(r.product_url) + '" aria-label="View product"><img src="' + escapeAttr(r.product_image_url) + '" alt="" loading="lazy" decoding="async" onerror="this.parentElement.remove();"></a>'
        : r.product_image_url
          ? '<span class="rw-card__product-thumb"><img src="' + escapeAttr(r.product_image_url) + '" alt="" loading="lazy" decoding="async" onerror="this.parentElement.remove();"></span>'
          : '';
      const cardClass = 'rw-card' + (productThumb ? ' rw-card--with-thumb' : '');
      return ''
        + '<article class="' + cardClass + '" data-review-card data-card-index="' + idx + '" tabindex="0" role="button" aria-label="Read full review">'
        + '  <div class="rw-card__head">'
        + '    <span class="rw-stars rw-stars--card">' + stars + '</span>'
        + '    <span class="rw-item-date">' + escapeHtml(date) + '</span>'
        +      productThumb
        + '  </div>'
        +    title
        + '  <p class="rw-card__body">' + escapeHtml(r.body || '') + '</p>'
        + (photos ? '<div class="rw-card__photos">' + photos + '</div>' : '')
        + '  <div class="rw-card__foot">'
        + '    <span class="rw-item-author">— ' + escapeHtml(r.reviewer_name || 'Anonymous') + '</span>'
        +      verifiedTag
        +      sourceTag
        +      productLink
        + '    <span class="rw-card__expand-hint">Read full →</span>'
        + '  </div>'
        + '</article>';
    }

    // ── Keyboard support: Enter / Space on a focused card opens modal ──
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('[data-review-card]');
      if (!card) return;
      e.preventDefault();
      const idx = parseInt(card.dataset.cardIndex, 10);
      if (Number.isFinite(idx)) openModal(idx);
    });

    // ── Modal: full-review popup with prev/next navigation ────────────
    function openModal(initialIdx) {
      let currentIdx = initialIdx;

      const overlay = document.createElement('div');
      overlay.className = 'rw-modal';
      overlay.innerHTML = ''
        + '<button type="button" class="rw-modal__close" data-rw-close aria-label="Close">×</button>'
        + '<button type="button" class="rw-modal__nav rw-modal__nav--prev" data-rw-prev aria-label="Previous review">‹</button>'
        + '<button type="button" class="rw-modal__nav rw-modal__nav--next" data-rw-next aria-label="Next review">›</button>'
        + '<div class="rw-modal__content" data-rw-content></div>'
        + '<div class="rw-modal__counter" data-rw-counter></div>';

      // Inherit the section's CSS vars so the popup picks up colors.
      overlay.style.setProperty('--rw-modal-bg', getComputedStyle(root).getPropertyValue('--rw-modal-bg').trim() || '#fff');
      overlay.style.setProperty('--rw-modal-overlay', getComputedStyle(root).getPropertyValue('--rw-modal-overlay').trim() || 'rgba(0,0,0,0.8)');
      overlay.style.setProperty('--rw-modal-radius', getComputedStyle(root).getPropertyValue('--rw-modal-radius').trim() || '12px');
      overlay.style.setProperty('--rw-modal-max-width', getComputedStyle(root).getPropertyValue('--rw-modal-max-width').trim() || '720px');
      overlay.style.setProperty('--rw-text', getComputedStyle(root).getPropertyValue('--rw-text').trim() || '#1f2a44');
      overlay.style.setProperty('--rw-text-muted', getComputedStyle(root).getPropertyValue('--rw-text-muted').trim() || '#6b7280');
      overlay.style.setProperty('--rw-divider', getComputedStyle(root).getPropertyValue('--rw-divider').trim() || '#e5e7eb');
      overlay.style.setProperty('--rw-star-on', getComputedStyle(root).getPropertyValue('--rw-star-on').trim() || '#f5a623');
      overlay.style.setProperty('--rw-star-off', getComputedStyle(root).getPropertyValue('--rw-star-off').trim() || '#e5e7eb');
      overlay.style.setProperty('--rw-verified-bg', getComputedStyle(root).getPropertyValue('--rw-verified-bg').trim() || '#e6f4ea');
      overlay.style.setProperty('--rw-verified-text', getComputedStyle(root).getPropertyValue('--rw-verified-text').trim() || '#1f7a3f');
      overlay.style.setProperty('--rw-etsy-bg', getComputedStyle(root).getPropertyValue('--rw-etsy-bg').trim() || '#fef3e2');
      overlay.style.setProperty('--rw-etsy-text', getComputedStyle(root).getPropertyValue('--rw-etsy-text').trim() || '#a0581c');

      function render() {
        const r = state.reviews[currentIdx];
        if (!r) return;
        const stars = renderStars(r.rating, 'card');
        const date = formatDate(r.created_at);
        const title = r.title ? '<h3 class="rw-modal__title">' + escapeHtml(r.title) + '</h3>' : '';
        const photos = (r.images || []).map(function (img) {
          return '<img class="rw-modal__photo" data-rw-modal-photo data-full-src="' + escapeAttr(img.cdn_url) + '" src="' + escapeAttr(img.cdn_url) + '" alt="" loading="lazy" decoding="async" onerror="this.remove();">';
        }).join('');
        const sourceTag = r.source === 'etsy_import'
          ? '<span class="rw-source-tag">via Etsy</span>' : '';
        const verifiedTag = r.verified_purchase
          ? '<span class="rw-verified-tag">✓ Verified</span>' : '';
        // "View product →" link — only when this review is attached to a
        // Yuki design/set (and Worker returned a product_url). The modal's
        // own click handler ignores generic anchors, so the <a> navigates
        // normally without dispatching the close/prev/next logic.
        const productLink = r.product_url
          ? '<a class="rw-product-link" href="' + escapeAttr(r.product_url) + '">View product →</a>' : '';
        // Product hero thumbnail — rendered inside the modal head as a
        // flex sibling of stars+date (same pattern as the list-layout
        // item, just bigger to suit the wider modal). Wrapped in the
        // product anchor when product_url is available; falls back to a
        // non-clickable <span> if only the image is known.
        const productThumb = r.product_image_url && r.product_url
          ? '<a class="rw-modal__product-thumb rw-product-link" href="' + escapeAttr(r.product_url) + '" aria-label="View product"><img src="' + escapeAttr(r.product_image_url) + '" alt="" loading="lazy" decoding="async" onerror="this.parentElement.remove();"></a>'
          : r.product_image_url
            ? '<span class="rw-modal__product-thumb"><img src="' + escapeAttr(r.product_image_url) + '" alt="" loading="lazy" decoding="async" onerror="this.parentElement.remove();"></span>'
            : '';
        const headClass = 'rw-modal__head' + (productThumb ? ' rw-modal__head--with-thumb' : '');
        overlay.querySelector('[data-rw-content]').innerHTML = ''
          + '<div class="' + headClass + '">'
          + '  <span class="rw-stars rw-stars--card" style="font-size:1.3em;">' + stars + '</span>'
          + '  <span class="rw-item-date">' + escapeHtml(date) + '</span>'
          +    productThumb
          + '</div>'
          + title
          + '<p class="rw-modal__body">' + escapeHtml(r.body || '') + '</p>'
          + (photos ? '<div class="rw-modal__photos">' + photos + '</div>' : '')
          + '<div class="rw-modal__foot">'
          + '  <span class="rw-item-author">— ' + escapeHtml(r.reviewer_name || 'Anonymous') + '</span>'
          + verifiedTag
          + sourceTag
          + productLink
          + '</div>';

        const prevBtn = overlay.querySelector('[data-rw-prev]');
        const nextBtn = overlay.querySelector('[data-rw-next]');
        prevBtn.disabled = currentIdx <= 0;
        // Next is enabled if (a) we're not at the end of the loaded set,
        // OR (b) we could load more pages.
        const canLoadMore = state.page < state.totalPages;
        nextBtn.disabled = (currentIdx >= state.reviews.length - 1) && !canLoadMore;

        overlay.querySelector('[data-rw-counter]').textContent = (currentIdx + 1) + ' / ' + state.total;

        // Scroll the modal's own content back to the top whenever the
        // review changes.
        const contentEl = overlay.querySelector('[data-rw-content]');
        if (contentEl) contentEl.scrollTop = 0;
      }

      async function go(delta) {
        const targetIdx = currentIdx + delta;
        if (targetIdx < 0) return;
        // If next would go past the loaded set, try to load the next
        // page first.
        if (targetIdx >= state.reviews.length) {
          if (state.page >= state.totalPages) return;
          const nextBtn = overlay.querySelector('[data-rw-next]');
          if (nextBtn) { nextBtn.disabled = true; nextBtn.textContent = '…'; }
          try {
            const data = await fetchByMode(spec, state.page + 1, perPage);
            state.reviews = state.reviews.concat(data.reviews);
            state.page += 1;
            renderList();          // refresh background cards too
            renderPagination();
          } catch (err) {
            console.warn('[reviews-widget] modal load-more failed:', err);
            if (nextBtn) { nextBtn.disabled = false; nextBtn.textContent = '›'; }
            return;
          } finally {
            if (nextBtn) nextBtn.textContent = '›';
          }
        }
        if (targetIdx < state.reviews.length) {
          currentIdx = targetIdx;
          render();
        }
      }

      function close() {
        document.removeEventListener('keydown', onKey);
        overlay.remove();
        document.body.classList.remove('rw-modal-open');
      }

      function onKey(e) {
        if (e.key === 'Escape')      { e.preventDefault(); close(); }
        else if (e.key === 'ArrowLeft')  { e.preventDefault(); go(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      }

      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) { close(); return; }
        if (e.target.closest('[data-rw-close]')) { close(); return; }
        if (e.target.closest('[data-rw-prev]'))  { go(-1); return; }
        if (e.target.closest('[data-rw-next]'))  { go(1); return; }
        const photo = e.target.closest('[data-rw-modal-photo]');
        if (photo) {
          openLightbox(photo.dataset.fullSrc, '');
          return;
        }
      });

      document.body.classList.add('rw-modal-open');
      document.body.appendChild(overlay);
      document.addEventListener('keydown', onKey);
      render();

      // Focus the next arrow so keyboard users can immediately ← →.
      const initialFocus = overlay.querySelector('[data-rw-next]');
      if (initialFocus) initialFocus.focus();
    }

    function renderPagination() {
      const pagEl = root.querySelector('[data-reviews-pagination]');
      if (!pagEl) return;
      // Carousel widgets manage navigation via arrow buttons, not a
      // "Load more" link. Always hide pagination for them.
      if (carousel) { pagEl.innerHTML = ''; return; }
      // URL-based pagination (dedicated reviews page): show prev/next
      // page anchors so the URL stays bookmarkable.
      if (urlPagination) {
        if (state.totalPages <= 1) { pagEl.innerHTML = ''; return; }
        const prev = state.page - 1;
        const next = state.page + 1;
        const prevDisabled = prev < 1;
        const nextDisabled = next > state.totalPages;
        pagEl.innerHTML = ''
          + '<div class="rw-pager">'
          + '<a class="rw-pager-link' + (prevDisabled ? ' is-disabled' : '') + '"'
          +   (prevDisabled ? ' aria-disabled="true" tabindex="-1"' : ' href="' + escapeAttr(buildPagerHref(prev)) + '"') + '>'
          + '← Previous</a>'
          + '<span class="rw-pager-count">Page ' + state.page + ' of ' + state.totalPages + '</span>'
          + '<a class="rw-pager-link' + (nextDisabled ? ' is-disabled' : '') + '"'
          +   (nextDisabled ? ' aria-disabled="true" tabindex="-1"' : ' href="' + escapeAttr(buildPagerHref(next)) + '"') + '>'
          + 'Next →</a>'
          + '</div>';
        return;
      }
      const loaded = state.reviews.length;
      if (loaded >= state.total) { pagEl.innerHTML = ''; return; }
      pagEl.innerHTML = '<button type="button" class="' + (isNewNs ? 'rw-load-more' : 'reviews-widget__load-more') + '" data-reviews-load-more>Load more reviews</button>';
    }

    /** Build a URL preserving all current query params except `page`. */
    function buildPagerHref(page) {
      const params = new URLSearchParams(window.location.search);
      params.set('page', String(page));
      return window.location.pathname + '?' + params.toString();
    }

    function reviewItemHtml(r) {
      const stars = renderStars(r.rating, 'card');
      const date = formatDate(r.created_at);
      // Photos: build with an onerror handler that hides the broken
      // anchor + logs to console so a 404 on R2 doesn't leave a silent
      // gray square. `loading="lazy"` is intentionally OMITTED for the
      // first review on the page — the user explicitly scrolled to see
      // reviews, so lazy isn't buying us anything and just delays the
      // load signal we'd see in DevTools Network.
      const photos = (r.images || []).map(function (img) {
        const onerr = "console.warn('[reviews-widget] image failed to load:', this.src); this.closest('a')?.remove();";
        return ''
          + '<a href="' + escapeAttr(img.cdn_url) + '" data-review-photo data-full-src="' + escapeAttr(img.cdn_url) + '" class="' + klass('photo') + '">'
          + '<img src="' + escapeAttr(img.cdn_url) + '" alt="" decoding="async" onerror="' + onerr + '"' + (img.width ? ' width="' + img.width + '"' : '') + (img.height ? ' height="' + img.height + '"' : '') + '>'
          + '</a>';
      }).join('');
      const title = r.title ? '<h4 class="' + klass('item-title') + '">' + escapeHtml(r.title) + '</h4>' : '';
      const sourceTag = r.source === 'etsy_import'
        ? '<span class="' + klass('source-tag') + '" title="Verified review imported from our Etsy shop">via Etsy</span>' : '';
      const verifiedTag = r.verified_purchase
        ? '<span class="' + klass('verified-tag') + '" title="Verified purchase">✓ Verified</span>' : '';
      // "View product" link — only when the review is attached to a
      // Yuki design or set. Worker resolves the URL from design_handle
      // (or wall_set_index lookup for sets).
      const productLink = r.product_url
        ? '<a class="rw-product-link" href="' + escapeAttr(r.product_url) + '">View product →</a>' : '';
      // Product hero thumbnail — rendered INSIDE the head as a flex
      // sibling of stars+date so the head expands vertically to fit the
      // thumb and the body/title/photos below get full-width back. The
      // previous absolute-positioned version overlapped multi-line body
      // text in the upper-right region of the item.
      // Wrapped in the product anchor when we also have product_url.
      const productThumb = r.product_image_url && r.product_url && isNewNs
        ? '<a class="rw-item__product-thumb rw-product-link" href="' + escapeAttr(r.product_url) + '" aria-label="View product"><img src="' + escapeAttr(r.product_image_url) + '" alt="" loading="lazy" decoding="async" onerror="this.parentElement.remove();"></a>'
        : (r.product_image_url && isNewNs)
          ? '<span class="rw-item__product-thumb"><img src="' + escapeAttr(r.product_image_url) + '" alt="" loading="lazy" decoding="async" onerror="this.parentElement.remove();"></span>'
          : '';
      const itemClass = klass('item') + (productThumb ? ' rw-item--with-thumb' : '');
      return ''
        + '<li class="' + itemClass + '">'
        + '  <div class="' + klass('item-head') + '">'
        + '    <span class="' + klass('stars') + ' ' + (isNewNs ? 'rw-stars--card' : '') + ' ' + klass('item-stars') + '">' + stars + '</span>'
        + '    <span class="' + klass('item-date') + '">' + escapeHtml(date) + '</span>'
        +      productThumb
        + '  </div>'
        +    title
        + '  <div class="' + klass('item-body') + '">' + escapeHtml(r.body || '') + '</div>'
        + (photos ? '<div class="' + klass('photos') + '">' + photos + '</div>' : '')
        + '  <div class="' + klass('item-foot') + '">'
        + '    <span class="' + klass('item-author') + '">— ' + escapeHtml(r.reviewer_name || 'Anonymous') + '</span>'
        +      verifiedTag
        +      sourceTag
        +      productLink
        + '  </div>'
        + '</li>';
    }

    function renderStars(rating, variant) {
      const rounded = Math.round(rating);
      let out = '';
      const klassStar = isNewNs ? 'rw-star' : 'reviews-widget__star';
      for (let i = 1; i <= 5; i++) {
        out += '<span class="' + klassStar + (i <= rounded ? ' is-filled' : '') + '">★</span>';
      }
      return out;
    }

    function formatDate(iso) {
      try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch { return ''; }
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c];
      });
    }
    function escapeAttr(s) { return escapeHtml(s); }

    function openLightbox(src, alt) {
      const overlay = document.createElement('div');
      overlay.className = isNewNs ? 'rw-lightbox' : 'reviews-widget__lightbox';
      const closeClass = isNewNs ? 'rw-lightbox-close' : 'reviews-widget__lightbox-close';
      overlay.innerHTML = ''
        + '<button type="button" class="' + closeClass + '" aria-label="Close">×</button>'
        + '<img src="' + escapeAttr(src) + '" alt="' + escapeAttr(alt) + '">';
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || e.target.classList.contains(closeClass)) overlay.remove();
      });
      document.body.appendChild(overlay);
    }
  }

  // ── JSON-LD enrichment ─────────────────────────────────────────────
  function injectIntoProductSchema(schemaId, state, reviews) {
    if (!schemaId || state.total === 0) return;
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (const s of scripts) {
      let parsed;
      try { parsed = JSON.parse(s.textContent); } catch { continue; }
      if (!parsed || parsed['@type'] !== 'Product' || parsed['@id'] !== schemaId) continue;
      parsed.aggregateRating = {
        '@type': 'AggregateRating',
        'ratingValue': Number((Math.round(state.avg * 10) / 10).toFixed(1)),
        'reviewCount': state.total,
        'bestRating': '5',
        'worstRating': '1',
      };
      parsed.review = (reviews || []).slice(0, 5).map(function (r) {
        return {
          '@type': 'Review',
          'author': { '@type': 'Person', 'name': r.reviewer_name || 'Anonymous' },
          'reviewRating': { '@type': 'Rating', 'ratingValue': r.rating, 'bestRating': '5', 'worstRating': '1' },
          'name': r.title || undefined,
          'reviewBody': r.body || r.title || '',
          'datePublished': r.created_at,
        };
      });
      s.textContent = JSON.stringify(parsed);
      return;
    }
  }
})();
